import { Separator } from '@vkontakte/vkui';
import debounce from 'lodash.debounce';
import { once } from 'ramda';
import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { RootState } from 'reactApp/store';
import { type CustomMarkerSliderHandler, CustomMarkerSlider } from 'reactApp/ui/CustomMarkerSlider/CustomMarkerSlider';
import { getContext, getStepByValue, marksValue, spaceFromGb } from 'reactApp/ui/FlexibleQuotaModal/FlexibleQuota.helpers';
import { type FlexibleQuotaFooterHandler, FlexibleQuotaFooter } from 'reactApp/ui/FlexibleQuotaModal/ui/Footer/FlexibleQuotaFooter';
import { FlexibleQuotaTooltip, THUMB_SATELLITE_ID } from 'reactApp/ui/FlexibleQuotaModal/ui/Tooltip/FlexibleQuotaTooltip';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './FlexibleQuotaBlock.css';

const minQuota = spaceFromGb(10);
const maxQuota = spaceFromGb(4000);

interface FlexibleQuotaBlockProps {
    source: string;
}

const sendAnalytic = debounce((source: string, count_gb: number) => {
    emitAnalyticEvent(AnalyticEventNames.FLEXIBLE_QUOTA_MODAL_CHANGE, { source, count_gb });
    sendPaymentGa({
        action: 'change',
        eventCategory: ECategoryGa.modal,
        name: `mnz_${source}`,
        count_gb,
    });
}, 300);

export function FlexibleQuotaBlock({ source }: FlexibleQuotaBlockProps) {
    const [value, setValue] = useState(minQuota.space);
    const initContextRef = useRef(true);
    const sliderRef = useRef<CustomMarkerSliderHandler | null>(null);
    const footerRef = useRef<FlexibleQuotaFooterHandler | null>(null);

    const originalValue = spaceFromGb(value).original;
    const flexibleTariffs = useSelector(ProductsSelectors.getWithFlexibleQuotaTarifffs);
    const product = useSelector((state: RootState) => ProductsSelectors.getProductWithFlexibleQuota(state, flexibleTariffs, originalValue));

    const handleChangeContext = useCallback(
        once(() => {
            initContextRef.current = false;
        }),
        []
    );

    const handleSliderChange = useCallback((value: number) => {
        sendAnalytic(source, value);
        handleChangeContext();
        setValue(value);
        footerRef.current?.setValue(value);
    }, []);

    const handleInputChange = useCallback((value = 0) => {
        sendAnalytic(source, value);
        handleChangeContext();
        setValue(value);
        sliderRef.current?.setValue(value);
    }, []);

    return (
        <div className={styles.root}>
            <CustomMarkerSlider
                ref={sliderRef}
                getStep={getStepByValue}
                marks={marksValue}
                min={minQuota.space}
                max={maxQuota.space}
                minValueLabel={minQuota.value}
                maxValueLabel={maxQuota.value}
                thumbSatelliteId={THUMB_SATELLITE_ID}
                thumbSatellite={<FlexibleQuotaTooltip value={value} context={getContext(value, initContextRef.current)} />}
                onChangeValue={handleSliderChange}
            />
            <Separator wide />
            <FlexibleQuotaFooter
                source={source}
                ref={footerRef}
                product={product}
                getStep={getStepByValue}
                maxSpace={maxQuota.space}
                minSpace={minQuota.space}
                onChange={handleInputChange}
            />
        </div>
    );
}
